import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const order = params.get("order") || "";
  const hintOk = params.get("status") === "success";
  const [state, setState] = useState({ loading: true, paid: hintOk, status: "" });

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!order) { setState({ loading: false, paid: hintOk, status: hintOk ? "paid" : "unknown" }); return; }
      try {
        // Server-side truth: reconciles with PayU (verify API) even if redirect was lost.
        const r = await api.get(`/payu/status/${order}`);
        if (!alive) return;
        if (r.data.paid) { setState({ loading: false, paid: true, status: "paid" }); return; }
        // Not paid → safe release (backend re-verifies before releasing the slot).
        await api.post(`/payu/release/${order}`).catch(() => {});
        if (!alive) return;
        setState({ loading: false, paid: false, status: r.data.status || "expired" });
      } catch {
        if (alive) setState({ loading: false, paid: hintOk, status: hintOk ? "paid" : "unknown" });
      }
    };
    run();
    return () => { alive = false; };
  }, [order, hintOk]);

  const { loading, paid } = state;

  return (
    <div className="min-h-screen bg-[#0c0616] text-slate-100 flex items-center justify-center px-4">
      <div className="glass rounded-3xl p-8 max-w-md w-full text-center" data-testid="payment-result">
        {loading ? (
          <div className="py-6">
            <Loader2 className="animate-spin text-purple-400 mx-auto" size={40} data-testid="payment-result-loading" />
            <p className="text-sm text-slate-400 mt-4">Confirming your payment with PayU…</p>
          </div>
        ) : (
          <>
            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 border ${paid ? "bg-emerald-500/20 border-emerald-500/40" : "bg-red-500/20 border-red-500/40"}`}>
              {paid ? <CheckCircle2 className="text-emerald-400" size={36} /> : <XCircle className="text-red-400" size={36} />}
            </div>
            <p className="font-display text-sm text-purple-300 uppercase tracking-widest">🎮 Play Station Gaming Café</p>
            <h2 className="font-display text-2xl font-bold mt-1" data-testid="payment-result-title">{paid ? "Payment Successful!" : "Payment Not Completed"}</h2>
            <p className="text-sm text-slate-400 mt-2">
              {paid
                ? "Your booking is confirmed and paid. See you at the café!"
                : "We didn't receive a completed payment, so the slot has been released. If money was deducted, it will auto-reverse — or contact the café and we'll verify it."}
            </p>
            {order && <p className="text-sm text-slate-400 mt-1">Order ID: <span className="font-mono text-purple-200" data-testid="payment-result-order">{order}</span></p>}
            <Link to="/"><Button data-testid="back-home-btn" className="w-full h-12 mt-6 bg-purple-600 hover:bg-purple-500 font-semibold">{paid ? "Back to Home" : "Book Again"}</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
